import React, { useState, useContext, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams, useNavigationType } from 'react-router-dom';
import { useUrlPage, useSyncUrlPage } from '@redpanda/utils';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/UserContext';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, ConfirmModal, ListPagination } from '@redpanda/ui';
import { BuildingLibraryIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import BankAccountDialog from '../components/BankAccountDialog';
import { TableSkeleton } from '@redpanda/ui';
import InvoiceLimitBanner from '../components/InvoiceLimitBanner';
import { useInvoiceLimitCache } from '../hooks/useInvoiceLimitCache';
import { toast } from 'react-hot-toast';
import { useBankAccountStore, useBankAccountStatsStore } from '../stores/bankAccountStore';

interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  currentBalance: number;
  isActive: boolean;
  createdAt?: string;
  _count?: {
    transactions: number;
  };
}

const BankAccountPage: React.FC = () => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const { isAuthenticated, isLoading: isUserLoading } = userContext!;
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BankAccount | null>(null);

  // 使用發票限制快取 hook
  const {
    canAddInvoice,
    setCanAddInvoice,
    limitBannerRefreshTrigger,
    setLimitBannerRefreshTrigger,
  } = useInvoiceLimitCache();

  const { stats, fetchStats } = useBankAccountStatsStore();

  const {
    accounts,
    pagination,
    isLoading: loading,
    isPageLoading,
    showDeleteConfirmModal,
    isDeleting,
    fetchAccounts,
    setCurrentPage,
    handleDelete,
    confirmDelete,
    closeDeleteModal,
  } = useBankAccountStore(() => fetchStats(true));

  // 網址 ?page=：從交易記錄返回時回到同一頁
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = { pathname, navigationType: useNavigationType(), searchParams, setSearchParams };
  const initialPage = useUrlPage(urlPage);

  // 使用快取載入，避免骨架閃爍
  useEffect(() => {
    if (isAuthenticated && !isUserLoading) {
      fetchAccounts(initialPage, false); // 使用快取，避免骨架閃爍
      fetchStats(false); // 統計數據可以使用快取
    }
  }, [isAuthenticated, isUserLoading, fetchAccounts, fetchStats]);

  // 監聽銀行帳戶總數變化，用於單個刪除時更新 InvoiceLimitBanner
  const prevAccountCountRef = useRef(accounts.length);
  useEffect(() => {
    const currentCount = accounts.length;
    const prevCount = prevAccountCountRef.current;

    // 如果帳戶數量減少（表示有刪除操作），觸發 InvoiceLimitBanner 更新
    if (currentCount < prevCount && prevCount > 0) {
      setLimitBannerRefreshTrigger(prev => prev + 1);
    }

    prevAccountCountRef.current = currentCount;
  }, [accounts.length, setLimitBannerRefreshTrigger]);

  const displayAccounts = Array.isArray(accounts) ? accounts : [];

  // 處理關閉對話框
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingAccount(null);
  };

  // 處理編輯
  const handleEdit = (account: BankAccount) => {
    setEditingAccount(account);
    setIsDialogOpen(true);
  };

  // 處理新增
  const handleAdd = () => {
    if (!canAddInvoice) {
      toast.error(t('bankAccounts.list.limitExceeded'));
      return;
    }
    setEditingAccount(null);
    setIsDialogOpen(true);
  };

  // 處理分頁
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchAccounts(newPage);
  };
  useSyncUrlPage(urlPage, pagination, handlePageChange);

  // 安全的刪除處理函數
  const handleSafeDelete = (account: BankAccount) => {
    if (account._count && account._count.transactions > 0) {
      return; // 有交易記錄的帳戶無法刪除
    }
    handleDelete(account.id);
  };

  // 導航到交易記錄頁面並傳遞 account 資料
  const handleNavigateToTransactions = (account: BankAccount) => {
    navigate(`/bank-accounts/${account.id}`, {
      state: { account }
    });
  };

  // 格式化帳戶類型
  const formatAccountType = (type: string) => {
    return t(`bankAccounts.types.${type}`, type);
  };

  // 格式化金額
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount);
  };


  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      {/* 頁面標題 */}
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('bankAccounts.list.title')}</h1>
          <button
            onClick={handleAdd}
            disabled={!canAddInvoice}
            className={`font-bold py-2 px-4 rounded transition-all duration-200 ${
              canAddInvoice
                ? 'bg-brand-600 hover:bg-brand-700 text-white cursor-pointer'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            title={canAddInvoice ? undefined : t('bankAccounts.list.limitExceeded')}
          >
            {t('bankAccounts.list.addButton')}
          </button>
        </div>
      </div>

      {/* 發票限制橫幅 */}
      <InvoiceLimitBanner onLimitChange={setCanAddInvoice} refreshTrigger={limitBannerRefreshTrigger} />

      {/* 統計概覽 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="text-brand-600 dark:text-brand-400">
                <BuildingLibraryIcon className="h-8 w-8" />
              </div>
            </div>
            <div className="ml-4 w-full">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{t('bankAccounts.stats.totalAccounts')}</h3>
              <div className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white min-h-[40px] flex items-center">
                {stats?.totalAccounts ?? 0}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="text-brand-600 dark:text-brand-400">
                <CheckCircleIcon className="h-8 w-8" />
              </div>
            </div>
            <div className="ml-4 w-full">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{t('bankAccounts.stats.activeAccounts')}</h3>
              <div className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white min-h-[40px] flex items-center">
                {stats?.activeAccounts ?? 0}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="text-brand-600 dark:text-brand-400">
                <div className="h-8 w-8 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold">
                  ¥
                </div>
              </div>
            </div>
            <div className="ml-4 w-full">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{t('bankAccounts.stats.totalBalance')}</h3>
              <div className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white min-h-[40px] flex items-center">
                {formatAmount(stats?.totalBalance ?? 0)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 帳戶列表 */}
      {(isPageLoading || loading) ? (
        <TableSkeleton columns={8} rows={5} />
      ) : (
        <div className="bg-white dark:bg-slate-800 shadow overflow-hidden rounded-md">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '800px' }}>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-1/6">{t('bankAccounts.table.bankName')}</TableHead>
                  <TableHead className="w-1/4">{t('bankAccounts.table.accountName')}</TableHead>
                  <TableHead className="w-1/6">{t('bankAccounts.table.accountNumber')}</TableHead>
                  <TableHead className="w-1/12">{t('bankAccounts.table.type')}</TableHead>
                  <TableHead className="w-1/6">{t('bankAccounts.table.balance')}</TableHead>
                  <TableHead className="w-1/12">{t('common.table.columns.status')}</TableHead>
                  <TableHead className="w-1/6">{t('common.table.columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayAccounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {t('bankAccounts.list.noAccounts')}
                  </TableCell>
                </TableRow>
              ) : (
                displayAccounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell className="w-1/6 font-medium">{account.bankName}</TableCell>
                    <TableCell className="w-1/4">{account.accountName}</TableCell>
                    <TableCell className="w-1/6 font-mono text-sm">
                      {account.accountNumber}
                    </TableCell>
                    <TableCell className="w-1/12">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100">
                        {formatAccountType(account.accountType)}
                      </span>
                    </TableCell>
                    <TableCell className="w-1/6 font-mono text-sm">
                      {formatAmount(account.currentBalance)}
                    </TableCell>
                    <TableCell className="w-1/12">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        account.isActive
                          ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                          : 'bg-gray-100 text-gray-800 dark:bg-slate-800 dark:text-white'
                      }`}>
                        {account.isActive ? t('common.status.active') : t('common.status.inactive')}
                      </span>
                    </TableCell>
                    <TableCell className="w-1/6">
                      <button
                        onClick={() => handleNavigateToTransactions(account)}
                        className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 mr-3 text-sm hover:underline"
                      >
                        {t('bankAccounts.list.transactionsButton')}
                      </button>
                      <button
                        onClick={() => handleEdit(account)}
                        className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300 mr-3 text-sm"
                      >
                        {t('common.actions.edit')}
                      </button>
                      <button
                        onClick={() => handleSafeDelete(account)}
                        disabled={account._count && account._count.transactions > 0}
                        title={account._count && account._count.transactions > 0 ? t('bankAccounts.list.cannotDeleteWithTransactions') : ''}
                        className={`text-sm ${
                          account._count && account._count.transactions > 0
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300'
                        }`}
                      >
                        {t('common.actions.delete')}
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* 分頁 */}
      <ListPagination pagination={pagination} onPageChange={handlePageChange} className="mt-6" />

      {/* 刪除確認對話框 */}
      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title={t('bankAccounts.list.confirmDeleteTitle')}
        message={t('bankAccounts.list.confirmDeleteMessage')}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      {/* 新增/編輯對話框 */}
      <BankAccountDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        account={editingAccount}
        onAccountCreated={() => {
          // 樂觀更新已在 BankAccountDialog 中完成，這裡只處理副作用
          fetchStats(true);
          setLimitBannerRefreshTrigger(prev => prev + 1);
        }}
        onAccountUpdated={() => {
          // 樂觀更新已在 BankAccountDialog 中完成，這裡只處理副作用
          fetchStats(true);
        }}
      />

    </div>
  );
};

export default BankAccountPage;